import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebaseConfig';
import { fixAdminAccess } from '../utils/fixAdminAccess';

interface Task {
  id: string;
  title: string;
  category: string;
  description?: string;
  type?: string;
  subcategory?: string;
  status?: string;
  createdAt?: any;
  updatedAt?: any;
}

interface ReferenceImage {
  id: string;
  taskId: string;
  taskTitle: string;
  originUrl: string;
  originHost?: string;
  status: 'queued' | 'processed' | 'error';
  error?: string;
  md5?: string;
  sha1?: string;
  phash?: string;
  thumbPath?: string;
  width?: number;
  height?: number;
  createdAt: any;
}

interface IngestResult {
  url: string;
  status: 'queued' | 'error';
  message: string;
  docId?: string;
}

interface TaskWithRefs {
  id: string;
  title: string;
  category: string;
  refCount: number;
  lastAdded?: any;
}

export default function ReferenceImages() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [taskTitle, setTaskTitle] = useState<string>('');
  const [urls, setUrls] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [ingestResults, setIngestResults] = useState<IngestResult[]>([]);
  const [referenceImages, setReferenceImages] = useState<ReferenceImage[]>([]);
  const [tasksWithRefs, setTasksWithRefs] = useState<TaskWithRefs[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'task'>('list');
  const [uploadFiles, setUploadFiles] = useState<FileList | null>(null);
  const [uploading, setUploading] = useState(false);
  const [taskSearchTerm, setTaskSearchTerm] = useState('');


  const handleFixAdminAccess = async () => {
    const result = await fixAdminAccess();
    if (result.success) {
      alert('✅ Admin access fixed! Please refresh the page.');
      window.location.reload();
    } else {
      alert(`❌ Error: ${result.error}`);
    }
  };

  // Load tasks
  useEffect(() => {
    console.log('🔍 [ReferenceImages] Starting to load tasks...');
    const q = query(collection(db, 'tasks'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('🔍 [ReferenceImages] Snapshot received:', snapshot.size, 'docs');
      const tasksData = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('🔍 [ReferenceImages] Task doc:', doc.id, data);
        return {
          id: doc.id,
          ...data
        };
      }) as Task[];
      console.log('🔍 [ReferenceImages] Final tasks array:', tasksData.length, tasksData);
      setTasks(tasksData);
    }, (error) => {
      console.error('🔍 [ReferenceImages] Error loading tasks:', error);
    });

    return () => unsubscribe();
  }, []);

  // Load tasks with reference image counts
  useEffect(() => {
    const q = query(collection(db, 'referenceImages'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const refImages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ReferenceImage[];
      
      // Group by taskId and count
      const taskRefCounts: { [taskId: string]: { count: number; lastAdded: any } } = {};
      refImages.forEach(ref => {
        if (!taskRefCounts[ref.taskId]) {
          taskRefCounts[ref.taskId] = { count: 0, lastAdded: null };
        }
        taskRefCounts[ref.taskId].count++;
        if (!taskRefCounts[ref.taskId].lastAdded || ref.createdAt > taskRefCounts[ref.taskId].lastAdded) {
          taskRefCounts[ref.taskId].lastAdded = ref.createdAt;
        }
      });

      // Create tasks with ref counts
      const tasksWithRefsData = tasks.map(task => ({
        id: task.id,
        title: task.title,
        category: task.category,
        refCount: taskRefCounts[task.id]?.count || 0,
        lastAdded: taskRefCounts[task.id]?.lastAdded
      })).filter(task => task.refCount > 0);

      setTasksWithRefs(tasksWithRefsData);
    });

    return () => unsubscribe();
  }, [tasks]);

  // Load reference images for selected task
  useEffect(() => {
    if (!selectedTaskId) {
      setReferenceImages([]);
      return;
    }

    const q = query(
      collection(db, 'referenceImages'),
      where('taskId', '==', selectedTaskId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const refImages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ReferenceImage[];
      setReferenceImages(refImages);
    });

    return () => unsubscribe();
  }, [selectedTaskId]);

  const handleIngest = async () => {
    if (!selectedTaskId || !taskTitle || !urls.trim()) {
      alert('Please select a task and enter URLs');
      return;
    }

    const urlList = urls
      .split('\n')
      .map(url => url.trim())
      .filter(url => url.length > 0);

    if (urlList.length === 0) {
      alert('Please enter at least one URL');
      return;
    }

    setLoading(true);
    setIngestResults([]);

    try {
      const refImagesIngest = httpsCallable(functions, 'refImagesIngest');
      const result = await refImagesIngest({
        taskId: selectedTaskId,
        taskTitle,
        urls: urlList
      });

      setIngestResults((result.data as any).results);
      setUrls(''); // Clear the textarea
    } catch (error) {
      console.error('Error ingesting reference images:', error);
      alert('Failed to ingest reference images. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedTaskId || !taskTitle || !uploadFiles || uploadFiles.length === 0) {
      alert('Please select a task and choose files to upload');
      return;
    }

    setUploading(true);
    const uploadResults: IngestResult[] = [];

    try {
      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i];
        
        // Validate file type
        if (!file.type.startsWith('image/') && !file.name.toLowerCase().endsWith('.zip')) {
          uploadResults.push({
            url: file.name,
            status: 'error',
            message: 'Not an image file or ZIP archive'
          });
          continue;
        }

        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          uploadResults.push({
            url: file.name,
            status: 'error',
            message: 'File too large (max 10MB)'
          });
          continue;
        }

        try {
          if (file.name.toLowerCase().endsWith('.zip')) {
            // Handle ZIP file - extract and process images
            uploadResults.push({
              url: file.name,
              status: 'error',
              message: 'ZIP file processing not yet implemented. Please extract and upload individual images.'
            });
            continue;
          }

          // Handle individual image files
          // Upload to Firebase Storage
          const storageRef = ref(storage, `reference-uploads/${selectedTaskId}/${Date.now()}-${file.name}`);
          const snapshot = await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(snapshot.ref);

          // Create reference image document
          const refImagesIngest = httpsCallable(functions, 'refImagesIngest');
          const result = await refImagesIngest({
            taskId: selectedTaskId,
            taskTitle,
            urls: [downloadURL]
          });

          if (result.data && (result.data as any).results) {
            const ingestResult = (result.data as any).results[0];
            uploadResults.push({
              url: file.name,
              status: ingestResult.status,
              message: ingestResult.message,
              docId: ingestResult.docId
            });
          } else {
            uploadResults.push({
              url: file.name,
              status: 'error',
              message: 'Failed to process image'
            });
          }
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          uploadResults.push({
            url: file.name,
            status: 'error',
            message: `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
          });
        }
      }

      setIngestResults(uploadResults);
      setUploadFiles(null);
      
      // Reset file input
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('Error during file upload:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const selectTask = (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    setSelectedTaskId(taskId);
    setTaskTitle(task?.title || '');
    setViewMode('task');
  };

  // Filter tasks based on search term
  const filteredTasks = tasks.filter(task => {
    if (!taskSearchTerm) return true;
    const searchLower = taskSearchTerm.toLowerCase();
    return (
      task.title.toLowerCase().includes(searchLower) ||
      task.category.toLowerCase().includes(searchLower) ||
      (task.description && task.description.toLowerCase().includes(searchLower)) ||
      (task.type && task.type.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Reference Images</h1>
            <p className="text-gray-600">
              Upload reference images for duplicate detection. These images are used only for fraud detection and never for training or export.
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded-md ${
                viewMode === 'list' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Task List
            </button>
            <button
              onClick={() => setViewMode('task')}
              className={`px-4 py-2 rounded-md ${
                viewMode === 'task' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Add Images
            </button>
            <button
              onClick={handleFixAdminAccess}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Fix Admin Access
            </button>
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Important Notice
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                Reference images are used <strong>only for duplicate/fraud detection</strong>. 
                They are <strong>never used for training or export</strong> and will not appear in any dataset exports.
              </p>
            </div>
          </div>
        </div>
      </div>

      {viewMode === 'list' ? (
        /* Task List View */
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Tasks with Reference Images</h2>
                <p className="text-sm text-gray-600 mt-1">Click on a task to view and manage its reference images</p>
              </div>
              <div className="w-64">
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={taskSearchTerm}
                  onChange={(e) => setTaskSearchTerm(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
          
          {tasksWithRefs.length === 0 ? (
            <div className="p-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No reference images yet</h3>
              <p className="mt-1 text-sm text-gray-500">Start by adding reference images to tasks.</p>
              <div className="mt-6">
                <button
                  onClick={() => setViewMode('task')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Add Reference Images
                </button>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {tasksWithRefs
                .filter(task => {
                  if (!taskSearchTerm) return true;
                  const searchLower = taskSearchTerm.toLowerCase();
                  return (
                    task.title.toLowerCase().includes(searchLower) ||
                    task.category.toLowerCase().includes(searchLower)
                  );
                })
                .map((task) => (
                <div key={task.id} className="p-6 hover:bg-gray-50 cursor-pointer" onClick={() => selectTask(task.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">{task.title}</h3>
                      <p className="text-sm text-gray-500">{task.category}</p>
                      <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <svg className="flex-shrink-0 mr-1.5 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                          </svg>
                          {task.refCount} reference image{task.refCount !== 1 ? 's' : ''}
                        </span>
                        {task.lastAdded && (
                          <span>Last added: {new Date(task.lastAdded.seconds * 1000).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Task View - Add Images */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Add Images */}
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Add Reference Images</h2>
              
              {/* Task Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Task
                </label>
                
                {/* Search Input */}
                <div className="mb-3">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search tasks by title, category, or description..."
                      value={taskSearchTerm}
                      onChange={(e) => setTaskSearchTerm(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {taskSearchTerm && (
                      <button
                        onClick={() => setTaskSearchTerm('')}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {taskSearchTerm && (
                    <div className="mt-1 text-xs text-gray-500">
                      Showing {filteredTasks.length} of {tasks.length} tasks
                    </div>
                  )}
                </div>

                {/* Task Dropdown */}
                <select
                  value={selectedTaskId}
                  onChange={(e) => {
                    setSelectedTaskId(e.target.value);
                    const selectedTask = tasks.find(t => t.id === e.target.value);
                    setTaskTitle(selectedTask?.title || '');
                  }}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a task...</option>
                  {filteredTasks.map(task => (
                    <option key={task.id} value={task.id}>
                      {task.title} ({task.category})
                    </option>
                  ))}
                </select>
                
                {filteredTasks.length === 0 && taskSearchTerm && (
                  <div className="mt-2 text-sm text-gray-500">
                    No tasks found matching "{taskSearchTerm}"
                  </div>
                )}
              </div>

              {/* File Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Images
                </label>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  accept="image/*,.zip"
                  onChange={(e) => setUploadFiles(e.target.files)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Select multiple images or ZIP files (max 10MB each)</p>
              </div>

              {/* URL Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or paste URLs (one per line)
                </label>
                <textarea
                  value={urls}
                  onChange={(e) => setUrls(e.target.value)}
                  placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg&#10;..."
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <button
                  onClick={handleFileUpload}
                  disabled={uploading || !selectedTaskId || !uploadFiles}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading...' : 'Upload Images'}
                </button>
                
                <button
                  onClick={handleIngest}
                  disabled={loading || !selectedTaskId || !urls.trim()}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Process URLs'}
                </button>
              </div>
            </div>

            {/* Batch Status */}
            {ingestResults.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Status</h3>
                <div className="space-y-2">
                  {ingestResults.map((result, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600 truncate">{result.url}</p>
                        <p className={`text-xs ${
                          result.status === 'queued' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {result.message}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        result.status === 'queued' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {result.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Current Task Images */}
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {selectedTaskId ? `${taskTitle} - Reference Images (${referenceImages.length})` : 'Reference Images'}
              </h3>
              
              {!selectedTaskId ? (
                <p className="text-gray-500 text-center py-8">Select a task to view reference images</p>
              ) : referenceImages.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No reference images found for this task</p>
              ) : (
                <div className="space-y-3">
                  {referenceImages.map((refImage) => (
                    <div key={refImage.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              refImage.status === 'processed' 
                                ? 'bg-green-100 text-green-800'
                                : refImage.status === 'queued'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {refImage.status}
                            </span>
                            {refImage.originHost && (
                              <span className="text-xs text-gray-500">{refImage.originHost}</span>
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-600 truncate mb-2">
                            {refImage.originUrl}
                          </p>
                          
                          {refImage.status === 'processed' && (
                            <div className="text-xs text-gray-500 space-y-1">
                              {refImage.md5 && <div>MD5: {refImage.md5.substring(0, 8)}...</div>}
                              {refImage.phash && <div>pHash: {refImage.phash.substring(0, 8)}...</div>}
                              {refImage.width && refImage.height && (
                                <div>Size: {refImage.width}×{refImage.height}</div>
                              )}
                            </div>
                          )}
                          
                          {refImage.status === 'error' && refImage.error && (
                            <p className="text-xs text-red-600 mt-1">{refImage.error}</p>
                          )}
                        </div>
                        
                        {refImage.thumbPath && (
                          <div className="ml-4 flex-shrink-0">
                            <img
                              src={`https://storage.googleapis.com/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/${refImage.thumbPath}`}
                              alt="Thumbnail"
                              className="w-16 h-16 object-cover rounded border"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
